import {
    getComboItemsFrom,
    getProductItemsFrom,
    hasComboItemsIn,
    isComboInList,
    toComboCartLine
} from '../../helpers/cartComboHelpers';

export const cartSlice = (set, get) => ({
    items: [],
    productRevision: 0,
    // Server-issued CAS token (D3-B). Distinct from productRevision above:
    // this one is never incremented locally, only ever set from a Server
    // response, and is never persisted (see store.js partialize).
    cartRevision: 0,
    setCartRevision: (value) => set((state) => ({ ...state, cartRevision: value })),
    itemAdded: false,
    setItemAdded: (newItemAdded) => set(state => ({ ...state, itemAdded: newItemAdded })),
    getItemAdded: () => get().itemAdded,
    // Product ObjectIds and Combo ObjectIds come from different Mongo
    // collections, so a collision (however unlikely) is not ruled out by
    // contract. Every generic Product mutation below excludes Combo lines
    // explicitly instead of matching on `_id` alone, so a colliding Combo
    // can never be edited/deleted as if it were the Product.
    addItem: (newItem, currentQuantity) => set((state) => {
        if (
            !Number.isInteger(newItem?.quantityToBuy)
            || newItem.quantityToBuy <= 0
            || !Number.isInteger(currentQuantity)
            || currentQuantity <= 0
        ) {
            return { ...state, itemAdded: false };
        }
        const stateItems = [...state.items];
        const objFinded = stateItems.find((item) => item.itemType !== 'combo' && item._id === newItem._id);
        if (!stateItems.length || !objFinded) {
            const quantityToBuy = Math.min(newItem.quantityToBuy, currentQuantity);
            return {
                ...state,
                items: [...stateItems, { ...newItem, quantityToBuy }],
                itemAdded: quantityToBuy === newItem.quantityToBuy,
                productRevision: state.productRevision + 1
            };
        }
        let updatedItemAdded = true;
        const updatedItems = stateItems.map((item) => {
            if (item.itemType === 'combo' || item._id !== newItem._id) return item;
            const sumQuantityToBuy = item.quantityToBuy + newItem.quantityToBuy;
            const objToReturn = {
                ...item,
                quantityToBuy: sumQuantityToBuy >= currentQuantity ? currentQuantity : sumQuantityToBuy
            };
            if (sumQuantityToBuy > currentQuantity) updatedItemAdded = false;
            return objToReturn;
        });
        return { ...state, items: [...updatedItems], itemAdded: updatedItemAdded, productRevision: state.productRevision + 1 }
    }),
    updateQuantity: (_id, quantityToBuy) => set(state => {
        if (!state.items.some((item) => item.itemType !== 'combo' && item._id === _id)) return state;
        const stateItems = state.items.map((item) => (
            (item.itemType !== 'combo' && item._id === _id) ? { ...item, quantityToBuy } : item
        ));
        return { ...state, items: stateItems, productRevision: state.productRevision + 1 }
    }),
    deleteItem: (itemID) => set(state => {
        const updatedItems = state.items.filter(item => item.itemType === 'combo' || item._id !== itemID);
        return { ...state, items: [...updatedItems], productRevision: state.productRevision + 1 }
    }),
    // Removes exactly the quantity that was part of a confirmed purchase,
    // never a blanket clear. A Product line is decremented by the purchased
    // quantity (a unit added to the same _id while the request was in flight
    // survives as leftover quantity, instead of being deleted along with the
    // purchased units); a Combo line is dropped outright (its quantity is
    // always 1 by design — see addCombo below).
    removePurchasedLines: (purchasedProducts = [], comboIds = []) => set((state) => ({
        ...state,
        items: state.items.reduce((acc, item) => {
            if (item.itemType === 'combo') {
                if (comboIds.includes(item._id)) return acc;
                return [...acc, item];
            }
            const purchased = purchasedProducts.find((p) => p._id === item._id);
            if (!purchased) return [...acc, item];
            const remaining = item.quantityToBuy - purchased.quantityToBuy;
            return remaining > 0 ? [...acc, { ...item, quantityToBuy: remaining }] : acc;
        }, []),
        productRevision: state.productRevision + 1
    })),
    updateItems: (updatedItems) => set(state => ({ ...state, items: [...updatedItems], productRevision: state.productRevision + 1 })),
    getSubtotal: () => get().items.reduce((acc, item) => {
        if (item.itemType === 'combo') {
            acc += (Number(item.specialPrice) || 0) * (Number(item.quantityToBuy) || 0);
            return acc;
        }
        acc += (item.price * item.quantityToBuy) - (!!item.discount && item.price * item.quantityToBuy * item.discount / 100);
        return acc;
    }, 0),
    setItems: (newItems) => set(state => ({ ...state, items: [...newItems], productRevision: state.productRevision + 1 })),
    getItems: () => get().items,
    replaceProductsFromServer: (products) => set((state) => ({
        ...state,
        items: [...products, ...getComboItemsFrom(state.items)]
    })),
    clearProductItems: () => set((state) => ({
        ...state,
        items: getComboItemsFrom(state.items),
        productRevision: state.productRevision + 1
    })),
    // Combos never touch the Server, so clearing them after a confirmed
    // purchase is purely local and must not bump productRevision/cartRevision.
    clearComboItems: () => set((state) => ({
        ...state,
        items: getProductItemsFrom(state.items)
    })),
    // Combos (Sprint Combos 1.0C-B): a Combo is a single grouped cart line
    // (itemType 'combo') carrying its nested Services as they arrived from
    // the public DTO. Never duplicated, never split, quantityToBuy fixed at 1
    // for this visual-integration phase (the public catalog never reveals
    // the exact numeric stock, so no larger quantity can be safely offered).
    addCombo: (comboDto) => set((state) => {
        if (isComboInList(state.items, comboDto._id)) return state;
        return { ...state, items: [...state.items, toComboCartLine(comboDto)] };
    }),
    deleteCombo: (comboId) => set((state) => ({
        ...state,
        items: state.items.filter((item) => !(item.itemType === 'combo' && item._id === comboId))
    })),
    getComboItems: () => getComboItemsFrom(get().items),
    getProductItems: () => getProductItemsFrom(get().items),
    hasComboItems: () => hasComboItemsIn(get().items),
    isComboInCart: (comboId) => isComboInList(get().items, comboId)
});
